import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { useState, useEffect } from "react";
import API from "../api/api01";
import { toast } from "sonner";
import {
  DollarSign,
  CreditCard,
  TrendingUp,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
} from "lucide-react";
import { InvoiceDetailModal } from "./InvoiceDetailModal";
import { cn } from "../ui/utils";

interface Account {
  id: string;
  leadName: string;
  course: string;
  totalFees: number;
  paidAmount: number;
  balanceAmount: number;
  paymentStatus: "Paid" | "Partial" | "Pending" | "Overdue";
  paymentMethod: "Online" | "Cash" | "Cheque" | "DD";
  lastPaymentDate: string;
  dueDate: string;
  counselor: {
    name: string;
    avatar: string;
  };
}

export function LeadAccounts() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await API.get("/lead-accounts/");
      setAccounts(response.data || []);
    } catch (err) {
      console.error("Failed to fetch lead accounts", err);
      toast.error("Failed to load account data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Paid":
        return <Badge className="bg-green-600 text-white">Paid</Badge>;
      case "Partial":
        return <Badge className="bg-blue-600 text-white">Partial</Badge>;
      case "Pending":
        return <Badge className="bg-yellow-600 text-white">Pending</Badge>;
      case "Overdue":
        return <Badge className="bg-red-600 text-white">Overdue</Badge>;
      default:
        return null;
    }
  };

  const calculateStats = () => {
    const totalRevenue = accounts.reduce((sum, acc) => sum + acc.paidAmount, 0);
    const totalOutstanding = accounts.reduce((sum, acc) => sum + acc.balanceAmount, 0);
    const paidCount = accounts.filter(acc => acc.paymentStatus === "Paid").length;
    const overdueCount = accounts.filter(acc => acc.paymentStatus === "Overdue").length;

    return { totalRevenue, totalOutstanding, paidCount, overdueCount };
  };

  const stats = calculateStats();

  const filteredAccounts = accounts.filter((account) => {
    const matchesSearch =
      account.leadName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      account.course.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter =
      statusFilter === "all" || account.paymentStatus === statusFilter;

    return matchesSearch && matchesFilter;
  });

  return (
    <div className="p-4 md:p-6 space-y-6 bg-slate-50 dark:bg-[#020617] min-h-full">
      {/* Header */}
      <div>
        <h1>Lead Accounts</h1>
        <p className="text-muted-foreground mt-1">
          Manage financial accounts and payment tracking for leads
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: "Total Revenue", val: `₹${(stats.totalRevenue / 1000).toFixed(0)}k`, icon: TrendingUp, color: "text-green-500", bg: "bg-green-500/10", glow: "from-green-500/0 via-green-500 to-green-500/0", hoverBg: "from-green-500/10" },
          { label: "Outstanding", val: `₹${(stats.totalOutstanding / 1000).toFixed(0)}k`, icon: DollarSign, color: "text-orange-500", bg: "bg-orange-500/10", glow: "from-orange-500/0 via-orange-500 to-orange-500/0", hoverBg: "from-orange-500/10" },
          { label: "Fully Paid", val: stats.paidCount, icon: CheckCircle2, color: "text-blue-500", bg: "bg-blue-500/10", glow: "from-blue-500/0 via-blue-500 to-blue-500/0", hoverBg: "from-blue-500/10" },
          { label: "Overdue", val: stats.overdueCount, icon: AlertCircle, color: "text-red-500", bg: "bg-red-500/10", glow: "from-red-500/0 via-red-500 to-red-500/0", hoverBg: "from-red-500/10" }
        ].map(s => (
          <Card key={s.label} className="glass-card group relative overflow-hidden shadow-sm border-none bg-slate-50 dark:bg-slate-900/40">
            {/* Premium Deep Glass Highlight */}
            <div className={cn("absolute top-0 left-0 w-full h-[3px] transition-all duration-500 opacity-30 group-hover:opacity-100 bg-gradient-to-r", s.glow)} />
            <div className={cn("absolute inset-0 transition-all duration-700 opacity-0 group-hover:opacity-100 bg-gradient-to-br via-transparent to-transparent", s.hoverBg)} />
            
            {/* Darker Tint Overlay on Hover */}
            <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 dark:bg-white/5" />

            <CardContent className="p-6 relative z-10">
              <div className="flex items-center gap-3">
                <div className={cn("p-3 rounded-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg", s.bg)}>
                  <s.icon className={cn("h-6 w-6", s.color)} />
                </div>
                <div>
                  <p className="text-2xl font-bold group-hover:translate-x-1 transition-transform duration-300">{s.val}</p>
                  <p className="text-sm text-muted-foreground">{s.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
  <CardContent className="p-4">
    <div className="flex flex-col md:flex-row gap-4">
      {/* Search Input */}
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-slate-500" />
        <Input
          placeholder="Search accounts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 w-full rounded-lg bg-slate-50 dark:bg-[#020617] border border-slate-200 dark:border-slate-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Payment Status Select */}
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-full md:w-48 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 flex items-center">
          <Filter className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
          <SelectValue placeholder="Payment Status" />
        </SelectTrigger>
        <SelectContent className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md">
          <SelectItem value="all" className="hover:bg-gray-100 dark:hover:bg-gray-700">All Status</SelectItem>
          <SelectItem value="Paid" className="hover:bg-gray-100 dark:hover:bg-gray-700">Paid</SelectItem>
          <SelectItem value="Partial" className="hover:bg-gray-100 dark:hover:bg-gray-700">Partial</SelectItem>
          <SelectItem value="Pending" className="hover:bg-gray-100 dark:hover:bg-gray-700">Pending</SelectItem>
          <SelectItem value="Overdue" className="hover:bg-gray-100 dark:hover:bg-gray-700">Overdue</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </CardContent>
</Card>


      {/* Accounts Table */}
      <Card className="shadow-sm bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle>Account Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 dark:bg-[#020617] border-b border-slate-200 dark:border-slate-800">
                  <TableHead>Lead Name</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead className="text-right">Total Fees</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Due Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAccounts.map((account) => (
                  <TableRow key={account.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800/50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={account.counselor.avatar} />
                          <AvatarFallback>
                            {account.leadName.substring(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{account.leadName}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{account.course}</TableCell>
                    <TableCell className="text-right font-medium">
                      ₹{account.totalFees.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-green-600">
                      ₹{account.paidAmount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-orange-600">
                      ₹{account.balanceAmount.toLocaleString()}
                    </TableCell>
                    <TableCell>{getStatusBadge(account.paymentStatus)}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">
                      {account.dueDate}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full hover:bg-primary/5 hover:text-primary transition-all active:scale-95"
                        onClick={() => {
                          setSelectedAccount(account);
                          setIsInvoiceModalOpen(true);
                        }}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Invoice
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Detail Portal */}
      <InvoiceDetailModal
        isOpen={isInvoiceModalOpen}
        onOpenChange={setIsInvoiceModalOpen}
        account={selectedAccount}
      />
    </div>
  );
}
